import { isKnownOauthScope, type OauthConsentClient } from '@/types/oauth'

/**
 * Turns the wire vocabulary into something a consent screen can render.
 *
 * Scopes are written `area:level`. The areas are the product surfaces a
 * customer recognises; the levels are few on purpose, because every one of
 * them has to be explicable in one line before somebody hands an agent
 * standing authority over their account.
 */

/** Area order, matching the vocabulary order in the backend. */
export const OAUTH_SCOPE_AREAS = [
  'account',
  'billing',
  'dns',
  'domain',
  'handle',
  'hosting',
  'k8s',
  'mail',
  'registry',
  'storage',
  'vserver'
] as const

export type OauthScopeArea = (typeof OAUTH_SCOPE_AREAS)[number]

/** Level order within an area, least to most authority. */
export const OAUTH_SCOPE_LEVELS = [
  'read',
  'write',
  'delete',
  'purchase',
  'credentials',
  'exec',
  'console',
  'authcode',
  'transfer'
] as const

export type OauthScopeLevel = (typeof OAUTH_SCOPE_LEVELS)[number]

export type OauthScopeRisk = 'low' | 'medium' | 'high'

/**
 * Risk by level.
 *
 * Everything above `write` is high, and that is not badge inflation: each of
 * them survives the grant in some way. Credentials keep working after the
 * agent is disconnected, a delete is not undone by revoking anything, a
 * purchase spends real money, and an auth code or a transfer moves a domain
 * out of the account entirely.
 */
const LEVEL_RISK: Readonly<Record<OauthScopeLevel, OauthScopeRisk>> = {
  read: 'low',
  write: 'medium',
  delete: 'high',
  purchase: 'high',
  credentials: 'high',
  exec: 'high',
  console: 'high',
  authcode: 'high',
  transfer: 'high'
}

export interface OauthScopeEntry {
  /** The raw scope string, exactly as the client requested it. */
  scope: string
  area: string
  level: string
  /** False for a scope this build cannot describe. */
  known: boolean
  risk: OauthScopeRisk
}

export interface OauthScopeGroup {
  area: string
  /** False when the area itself is one this build does not know. */
  known: boolean
  scopes: OauthScopeEntry[]
}

const isKnownArea = (area: string): boolean =>
  (OAUTH_SCOPE_AREAS as readonly string[]).includes(area)

const isKnownLevel = (level: string): boolean =>
  (OAUTH_SCOPE_LEVELS as readonly string[]).includes(level)

/**
 * Splits one scope into its parts.
 *
 * An unrecognised scope is described rather than dropped, at the highest risk.
 * Hiding it would mean the customer approves authority the screen never showed
 * them, which is the one outcome a consent screen exists to prevent.
 */
export const describeScope = (scope: string): OauthScopeEntry => {
  const separator = scope.indexOf(':')
  const area = separator === -1 ? scope : scope.slice(0, separator)
  const level = separator === -1 ? '' : scope.slice(separator + 1)
  const known = isKnownOauthScope(scope)

  return {
    scope,
    area,
    level,
    known,
    risk: known ? (LEVEL_RISK[level as OauthScopeLevel] ?? 'high') : 'high'
  }
}

/**
 * Groups scopes by area, in vocabulary order, de-duplicated.
 *
 * Unknown areas come last, keeping the familiar ones where the customer
 * expects them instead of letting a client reorder the screen by inventing a
 * scope name.
 */
export const groupScopes = (scopes: readonly string[]): OauthScopeGroup[] => {
  const seen = new Set<string>()
  const byArea = new Map<string, OauthScopeEntry[]>()

  for (const scope of scopes) {
    if (seen.has(scope)) continue
    seen.add(scope)
    const entry = describeScope(scope)
    byArea.set(entry.area, [...(byArea.get(entry.area) ?? []), entry])
  }

  // Built by walking the vocabulary rather than by sorting what arrived, so
  // the screen reads the same whatever order a client listed its scopes in.
  // Unknown areas follow, in the order they were requested.
  const areas = [
    ...OAUTH_SCOPE_AREAS.filter((area) => byArea.has(area)),
    ...[...byArea.keys()].filter((area) => !isKnownArea(area))
  ]

  return areas.map((area) => {
    const entries = byArea.get(area) ?? []
    return {
      area,
      known: isKnownArea(area),
      scopes: [
        ...OAUTH_SCOPE_LEVELS.flatMap((level) => entries.filter((e) => e.level === level)),
        ...entries.filter((e) => !isKnownLevel(e.level))
      ]
    }
  })
}

/**
 * Whether the selection carries a spending scope that `billing:purchase` has
 * to unlock.
 *
 * Two keys turn that lock on the backend, so a selection holding
 * `domain:purchase` without `billing:purchase` buys nothing. Saying so on the
 * screen is better than handing back a grant that silently cannot do the thing
 * the customer just approved.
 */
export const needsBillingPurchase = (scopes: readonly string[]): boolean =>
  scopes.some((scope) => scope.endsWith(':purchase') && scope !== 'billing:purchase') &&
  !scopes.includes('billing:purchase')

/**
 * The host a `client_id` was checked against.
 *
 * The backend screens it and sends it, and that value wins. The fallback
 * re-derives it from a client_id that is itself an https URL, which is what a
 * client-ID metadata document is: the document has to be served from the URL it
 * names, so its host is the one fact about such a client that is not
 * self-asserted.
 *
 * The fallback is gated on `registration` rather than on the shape of the
 * client_id alone. A dynamically registered client has no verified host, and if
 * one ever managed to register a URL-shaped client_id, deriving a host from it
 * would put a self-chosen string in the place reserved for a checked fact.
 */
export const verifiedClientHost = (client: OauthConsentClient): string | null => {
  if (client.host) return client.host
  if (client.registration !== 'cimd' || !client.client_id.startsWith('https://')) return null

  try {
    const parsed = new URL(client.client_id)
    if (parsed.username || parsed.password || parsed.hash) return null
    return parsed.host || null
  } catch {
    return null
  }
}

/**
 * Hosts we operate. A tenant can publish a metadata document on one of these,
 * so a host ending here is ours in name only.
 */
const PLATFORM_HOST_SUFFIXES = ['.itsh.dev', '.itsh-apps.dev'] as const

/**
 * Whether a verified host sits on our own platform rather than belonging to
 * the client's operator.
 *
 * Every customer with a namespace gets a hostname under these suffixes, so a
 * client can publish its metadata document at `connect.tenant-42.itsh.dev` and
 * have our own domain appear in bold under "verified host": exactly the fact
 * the screen tells the customer to rely on. Renders as a warning instead, for
 * anything that is not the first-party client.
 */
export const isPlatformHost = (host: string | null, client: OauthConsentClient): boolean => {
  if (!host || client.trust_level === 'first_party') return false
  const lower = host.toLowerCase()
  return PLATFORM_HOST_SUFFIXES.some((suffix) => lower.endsWith(suffix))
}
